/**
 * viewportTransform2d — the pure 2D-affine coordinate transform between paper
 * space and model space through a paper-space VIEWPORT entity.
 *
 * **THREE-free by design.** This module carries ONLY the planar affine
 * (center / scale / twist) math — `modelToPaper`, `paperToModel`,
 * `pointInViewport` — and the `ViewportLike` / `Point2D` / `PaperOrigin`
 * types. It deliberately imports nothing from `three`, so it can be consumed
 * by the headless vector PDF exporter (`apps/web/src/app/print/pdf/*`) without
 * pulling WebGL into a browser-bad path. The one package import,
 * `drawing-model`'s `viewportWcsViewCenter`, is THREE-free too (that package
 * has no `three` dependency at all) and is already a runtime import of both
 * consumers of this module — the scene3d render path
 * (`rendering/viewportRenderPass.ts`) and the PDF exporter
 * (`print/pdf/entityToPrimitives.ts`, `sheetRenderer.ts`) — so it adds no new
 * edge to either bundle. The constraint is about `three`, not about the CAD
 * core; the centre reading BELONGS in the CAD core (CLAUDE.md PRIORITY #1).
 *
 * The screen-unproject helper (`screenToModelCoords`) — which DOES need a
 * `THREE.OrthographicCamera` — stays in `viewportTransformAdapter.ts`, which
 * re-exports everything here so every existing import path keeps working
 * unchanged (CLAUDE.md PRIORITY #2 — dep-graph: barrel re-export, no caller
 * churn).
 *
 * AutoCAD parity: each VIEWPORT defines a rectangular window into model space.
 * The transform accounts for:
 *   - VP position on paper sheet (center + paperOrigin)
 *   - View scale (viewHeight / vpHeight = model units per paper unit)
 *   - View twist (viewTwistAngle, degrees, DXF group 51) — rotates model view
 *
 * UCS per-viewport / viewDir / perspective: NOT in `ViewportLike`; out of
 * scope for the 2D affine (planar projection only — see PLAN-plot-pdf.md §4).
 *
 * Pure functions — no side effects, heavily unit-tested.
 */

import { viewportWcsViewCenter } from "@dxf-ai-editor/drawing-model";

/** 2D point */
export type Point2D = { x: number; y: number };

/**
 * Paper-space sheet origin (`LayoutPageSetup.origin`).
 *
 * # Frame contract
 *
 * Every function in this module is frame-AGNOSTIC but frame-CONSISTENT: it
 * only ever uses DIFFERENCES of paper quantities — `paperPt − (viewport.center
 * + paperOrigin)` in `paperToModel`, `vpCenter ± half` compared against
 * `paperPt` in `viewportPaperRect` / `pointInViewport` — and a difference is
 * invariant under the pure translation that separates the DOCUMENT frame from
 * the renderer's OFFSET-world frame. So no arithmetic here changes with the
 * frame, PROVIDED the caller passes `paperPt` and `paperOrigin` in the SAME
 * one. Callers on the renderer/pointer side therefore pass the OFFSET-world
 * origin (`coordinateOrigin.applyOffsetToPaperOrigin`); the headless PDF
 * exporter, which works in raw document mm, passes the document-frame origin
 * with document-frame points.
 *
 * Note the asymmetry of the OUTPUTS: `modelToPaper` returns a point in
 * whatever frame `paperOrigin` was in, whereas `paperToModel` adds the
 * viewport's view centre — a DOCUMENT-frame model coordinate — to a frame-free
 * delta, so its result is always a document-frame MODEL point, needing no
 * un-offset before it meets other document-frame model geometry.
 *
 * # Which centre: WCS, not the stored 12/22 pair
 *
 * The centre added here is {@link viewportWcsViewCenter} — `viewTarget +
 * R(viewTwistAngle)·viewCenter` — NOT the bare `viewport.viewCenter`. Groups
 * 12/22 are the view centre in the DISPLAY coordinate system, whose origin is
 * the WCS view TARGET (17/27/37) and whose axes are turned by the view twist,
 * so the WCS point at the middle of the window is the rotated pair plus the
 * target (the plain per-axis sum when the twist is 0).
 * See that helper's module doc in `drawing-model` for the DXF derivation and
 * the AutoCAD-2018 measurement that settles it. An ABSENT target is the WCS
 * origin, i.e. `0`, so every zero-target viewport is byte-identical to the
 * pre-target arithmetic.
 *
 * This is not a free choice: the per-VP camera
 * (`rendering/viewportRenderPass.buildViewportModelCamera`), the cull rect
 * (`viewportCullingAdapter.computeViewportModelBounds`), the VPCLIP stencil
 * (`drawing-model/viewportClipFinalize.paperToViewportModel`) and the
 * membership index (`drawing-model/viewportMembership.computeViewportViewRect`)
 * all anchor on the SAME sum. Anchoring the pointer transform on the raw pair
 * instead would put every pick / OSNAP / window-select / draft preview /
 * plotted sheet a whole target vector away from what is painted — 5e5 units on
 * the reporting file.
 *
 * ## Consequence for WRITE-BACK — the trap this doc used to set
 *
 * `paperToModel`'s result is a document-frame **WCS** model point. The
 * ViewportEntity's `viewCenter` field stays **DCS** (the authored 12/22 pair,
 * which is what the Properties palette shows and what the DXF round-trip
 * preserves), so a pointer-derived centre CANNOT be written straight back into
 * it any more: it must first pass through
 * `drawing-model`'s `viewportStoredViewCenterFromWcs`, which subtracts the
 * target. `stageInteractions`' wheel-zoom is the one such write-back today
 * (zoom-around-cursor consumes the WCS centre, then un-offsets before
 * dispatching `geometry/viewport-set-view`).
 *
 * The in-VP PAN is the second such write-back and obeys the same rule:
 * `viewportZoomPan.panViewportByPixels` rotates its pixel delta by `R(+twist)`,
 * which is this function's own screen→model axis map, so it consumes and
 * returns WCS and its result is un-offset by the same helper. (It was once
 * exempt, on the argument that a delta is frame-free. That held only while
 * DCS↔WCS was a pure translation; the twist makes the frames differ by a
 * ROTATION as well, and a delta is not rotation-free.)
 */
export type PaperOrigin = { x: number; y: number };

export interface ViewportLike {
  center: { x: number; y: number };
  width: number;
  height: number;
  /**
   * DXF groups 12/22 — the view centre in the DISPLAY coordinate system, as
   * stored on the entity. NOT the WCS point at the middle of the window; pair
   * it with {@link ViewportLike.viewTarget} through `viewportWcsViewCenter`
   * (see the "Which centre" section of {@link PaperOrigin}'s doc).
   */
  viewCenter: { x: number; y: number };
  /**
   * DXF groups 17/27 — the WCS point the DCS origin sits at. OPTIONAL so
   * `Pick`-shaped literals and test stubs that predate it still compile; an
   * absent target is the WCS origin, which reproduces the pre-target
   * arithmetic exactly. Callers that build a SYNTHETIC `ViewportLike` from a
   * real `ViewportEntity` (`print/pdf/viewportPlot.toViewportLike`,
   * `Scene3DStage`'s `getActiveViewport`) MUST pass the entity's target
   * through — dropping it makes the plotted sheet / the pointer disagree with
   * the screen.
   */
  viewTarget?: { x: number; y: number } | undefined;
  viewHeight: number;
  viewTwistAngle: number; // degrees
}

/**
 * Transform a paper-space point to model space through a viewport.
 * Applies inverse VP transform: un-scale + un-rotate.
 *
 * Returns a document-frame **WCS** model point (`viewTarget + R(twist)·viewCenter`, plus
 * the rotated, scaled offset) — see {@link PaperOrigin}'s frame contract,
 * including why a write-back into the stored DCS `viewCenter` field must
 * subtract the target.
 */
export function paperToModel(
  paperPt: Point2D,
  viewport: ViewportLike,
  paperOrigin: PaperOrigin,
): Point2D {
  const vpCenterX = viewport.center.x + paperOrigin.x;
  const vpCenterY = viewport.center.y + paperOrigin.y;

  const pRelX = paperPt.x - vpCenterX;
  const pRelY = paperPt.y - vpCenterY;

  // Paper → model scale: model units per paper unit
  const scale = viewport.viewHeight / viewport.height;

  const vRelX = pRelX * scale;
  const vRelY = pRelY * scale;

  // Rotate by +θ to go from viewport-local back to model space
  const theta = (viewport.viewTwistAngle * Math.PI) / 180;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);

  const mRelX = vRelX * cosT - vRelY * sinT;
  const mRelY = vRelX * sinT + vRelY * cosT;

  // The WCS point at the middle of the window — `viewTarget + R(twist)·viewCenter`, the
  // ONE reading `drawing-model` owns and the camera / cull rect / VPCLIP
  // stencil / membership index all anchor on. Never the raw 12/22 pair.
  const center = viewportWcsViewCenter(viewport);

  return {
    x: center.x + mRelX,
    y: center.y + mRelY,
  };
}

/**
 * Transform a model-space point to paper space through a viewport.
 * Applies VP transform: rotate + scale.
 *
 * `modelPt` is a document-frame **WCS** model point — the exact inverse of
 * {@link paperToModel}, so it subtracts the same `viewTarget + R(twist)·viewCenter`.
 */
export function modelToPaper(
  modelPt: Point2D,
  viewport: ViewportLike,
  paperOrigin: PaperOrigin,
): Point2D {
  const center = viewportWcsViewCenter(viewport);
  const mRelX = modelPt.x - center.x;
  const mRelY = modelPt.y - center.y;

  // Rotate by −θ: model space → viewport local coords
  const theta = (viewport.viewTwistAngle * Math.PI) / 180;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);

  const vRelX = mRelX * cosT + mRelY * sinT;
  const vRelY = -mRelX * sinT + mRelY * cosT;

  // Model → paper scale
  const scale = viewport.height / viewport.viewHeight;

  const pRelX = vRelX * scale;
  const pRelY = vRelY * scale;

  const vpCenterX = viewport.center.x + paperOrigin.x;
  const vpCenterY = viewport.center.y + paperOrigin.y;

  return {
    x: vpCenterX + pRelX,
    y: vpCenterY + pRelY,
  };
}

/** Axis-aligned paper-space rectangle bounds of a viewport frame. */
export interface ViewportPaperRect {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * The viewport's axis-aligned paper-space frame bounds (center ± half-extent,
 * offset by the paper origin). The VP frame on the sheet is ALWAYS axis-aligned
 * — `viewTwistAngle` rotates the model view INSIDE the rectangle, not the
 * rectangle itself — so these four bounds exactly bound the frame regardless of
 * twist.
 *
 * Single source of the rect derivation: both `pointInViewport` (hit-test) and
 * the MC4 in-VP draft-preview clip planes (`resolveDraftPreviewMapping.clipRect`)
 * consume this, so the center±half/origin formula is never duplicated
 * (CLAUDE.md PRIORITY #2 — no jscpd dup, one geometric truth).
 */
export function viewportPaperRect(
  viewport: ViewportLike,
  paperOrigin: PaperOrigin,
): ViewportPaperRect {
  const vpCenterX = viewport.center.x + paperOrigin.x;
  const vpCenterY = viewport.center.y + paperOrigin.y;

  const halfW = viewport.width / 2;
  const halfH = viewport.height / 2;

  return {
    minX: vpCenterX - halfW,
    maxX: vpCenterX + halfW,
    minY: vpCenterY - halfH,
    maxY: vpCenterY + halfH,
  };
}

/**
 * Test whether a paper-space point falls inside the viewport's rectangular
 * boundary (the VP's paper-space frame is always axis-aligned — twist only
 * affects the model view, not the frame on the sheet).
 */
export function pointInViewport(
  paperPt: Point2D,
  viewport: ViewportLike,
  paperOrigin: PaperOrigin,
): boolean {
  const { minX, maxX, minY, maxY } = viewportPaperRect(viewport, paperOrigin);

  return (
    paperPt.x >= minX &&
    paperPt.x <= maxX &&
    paperPt.y >= minY &&
    paperPt.y <= maxY
  );
}

/**
 * Clip a paper-space segment `a→b` to an axis-aligned rectangle (Liang-Barsky).
 * Returns the trimmed `{ from, to }` (both endpoints on or inside the rect) or
 * `null` when the segment lies entirely outside.
 *
 * Used to clip the POLAR tracking guide overlay to the active viewport frame so
 * the dashed line never leaks across the layout sheet outside the VP (#492
 * @codex P2): the draft rubber band is trimmed at the VP edge by the render-side
 * clip planes (`resolveDraftPreviewMapping.clipRect`), but the polar guide is
 * drawn by the paper-camera overlay and needs the SAME rect trim in JS.
 */
export function clipSegmentToRect(
  a: Point2D,
  b: Point2D,
  rect: ViewportPaperRect,
): { from: Point2D; to: Point2D } | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  // Liang-Barsky edges: [p, q] per boundary (left, right, bottom, top). A
  // zero-length segment (dx=dy=0) has p=0 on every edge and is kept iff every q
  // is ≥ 0 (i.e. the point is inside), matching `pointInViewport`.
  const edges: ReadonlyArray<readonly [number, number]> = [
    [-dx, a.x - rect.minX],
    [dx, rect.maxX - a.x],
    [-dy, a.y - rect.minY],
    [dy, rect.maxY - a.y],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      // Parallel to this edge and outside its half-plane ⇒ fully clipped.
      if (q < 0) return null;
    } else {
      const r = q / p;
      if (p < 0) {
        if (r > t1) return null;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return null;
        if (r < t1) t1 = r;
      }
    }
  }
  if (t0 > t1) return null;
  return {
    from: { x: a.x + t0 * dx, y: a.y + t0 * dy },
    to: { x: a.x + t1 * dx, y: a.y + t1 * dy },
  };
}
